/* eslint-disable lit-a11y/click-events-have-key-events */
/* eslint-disable lit-a11y/anchor-is-valid */
import { LitElement, html, css, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import { getLocale } from "./localization";
import "@wh-web-front-end/wh-themes/dist/web/walgreens/light.css";
import "@wh-web-front-end/wh-component-library/dist/elements/wh-footer";
import "@wh-web-front-end/wh-component-library/dist/elements/wh-icon";
import "@wh-web-front-end/wh-component-library/dist/elements/wh-button";
import type { FooterContent } from "./types/content";

declare global {
  interface HTMLElementTagNameMap {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    "web-kiosk-footer": WebKioskFooterElement;
  }
}

const nextUpLocale = new Map([
  ["en", "es-419"],
  ["es-419", "en"],
]);
@customElement("web-kiosk-footer")
export class WebKioskFooterElement extends LitElement {
  @state() content: FooterContent | undefined = undefined;

  @property({ type: Boolean }) showEndSessionButton = false;

  switchLocale() {
    const locale = getLocale();
    const nextLocale = nextUpLocale.get(locale);
    if (nextLocale !== undefined && nextLocale !== "") {
      this.dispatchEvent(
        new CustomEvent("web-kiosk-change-locale", {
          detail: nextLocale,
          bubbles: true,
        })
      );
    }
  }

  navigate(path: string): void {
    this.dispatchEvent(
      new CustomEvent("web-kiosk-navigate", {
        detail: path,
        bubbles: true,
      })
    );
  }

  endSession(): void {
    // will change to payor-select-screen
    this.navigate("home");
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
  }

  renderLanguageButton(): TemplateResult {
    return html` <a
      @click=${() => {
        this.switchLocale();
      }}
    >
      <wh-icon name="globe"></wh-icon>
      ${this.content?.nextLanguage}
    </a>`;
  }

  renderEndSessionButton(): TemplateResult | typeof nothing {
    const showEndButton = this.showEndSessionButton;

    return showEndButton
      ? html` <wh-button
          id="end-session-button"
          variant="primary"
          size="large"
          @click=${() => {
            this.dispatchEvent(
              new CustomEvent("open-end-session-prompt", {
                bubbles: true,
                composed: true,
              })
            );
          }}
        >
          ${this.content?.endSession}
        </wh-button>`
      : nothing;
  }

  render(): TemplateResult {
    return html` <wh-footer>
      <section slot="inside-footer-content"></section>
      <section role="contentinfo" slot="outside-footer-content">
        <span class="navigation">
          <span class="left">
            <a
              @click=${() => {
                this.navigate("feedback");
              }}
            >
              <wh-icon name="thumbs-up"></wh-icon>
              ${this.content?.leaveFeedback}
            </a>
            <a
              @click=${() => {
                this.navigate("privacy");
              }}
            >
              <wh-icon name="privacy"></wh-icon>
              ${this.content?.privacyPolicy} &
              ${this.content?.termsAndConditions}
            </a>
          </span>
          <span class="right">
            ${this.renderLanguageButton()} ${this.renderEndSessionButton()}
          </span>
        </span>
      </section>
    </wh-footer>`;
  }

  static styles = css`
    :host {
      --web-kiosk-footer-bg: #f5f5f0;
      --web-kiosk-footer-border-color: #3232643d;
      --web-kiosk-link-color: #284b9b;
      --web-kiosk-brand-primary: #e52600;
    }

    section[slot="inside-footer-content"] {
      display: none;
    }

    section[slot="outside-footer-content"] {
      width: calc(100% - 64px * 2);
      padding: 18px 64px 16px;
      background-color: var(--web-kiosk-footer-bg, hotpink);
      border-top: 2px solid var(--web-kiosk-footer-border-color, hotpink);
    }

    .navigation {
      display: flex;
    }

    .left {
      display: flex;
      flex: 1 0;
    }

    .right {
      display: flex;
      align-items: center;
    }

    a {
      display: inline-flex;
      font-size: 28px;
      font-weight: 400;
      line-height: 48px;
      text-decoration: none;
      color: var(--web-kiosk-link-color, hotpink);
      cursor: pointer;
    }

    .left a {
      margin-right: 64px;
    }

    .right wh-button {
      margin-left: 64px;
    }

    .switch-language-link {
      margin-right: 64px;
    }

    wh-icon {
      font-size: 48px;
      margin-right: 8px;
    }

    #end-session-button::part(base) {
      background-color: var(--web-kiosk-brand-primary, hotpink);
      font-size: 24px;
      padding-left: 32px;
      padding-right: 32px;
    }
  `;
}
